import React from 'react'
import { Card, Row, Col, Statistic, Tag, Table, Button, Space, Typography, Progress, message } from 'antd'
import {
  ShopOutlined,
  RiseOutlined,
  FallOutlined,
  SyncOutlined,
  SettingOutlined,
  BarChartOutlined,
} from '@ant-design/icons'

const { Title, Text } = Typography

// 模拟平台数据
const platformsData = [
  {
    key: '1',
    name: 'Amazon Haul',
    type: 'haul',
    status: 'active',
    products: 2450,
    orders: 1234,
    revenue: 45678.90,
    growth: 12.5,
    quality_score: 85,
    last_sync: '2026-08-31 10:30:00',
  },
  {
    key: '2',
    name: 'Walmart',
    type: 'walmart',
    status: 'active',
    products: 1890,
    orders: 987,
    revenue: 34567.80,
    growth: 8.3,
    quality_score: 72,
    last_sync: '2026-08-31 10:25:00',
  },
  {
    key: '3',
    name: 'Joom',
    type: 'joom',
    status: 'active',
    products: 1560,
    orders: 756,
    revenue: 23456.70,
    growth: -2.1,
    quality_score: 68,
    last_sync: '2026-08-31 10:20:00',
  },
  {
    key: '4',
    name: 'Ozon',
    type: 'ozon',
    status: 'active',
    products: 1230,
    orders: 543,
    revenue: 18901.20,
    growth: 15.7,
    quality_score: 78,
    last_sync: '2026-08-31 10:15:00',
  },
  {
    key: '5',
    name: 'Shopify',
    type: 'shopify',
    status: 'active',
    products: 890,
    orders: 432,
    revenue: 15678.30,
    growth: 5.2,
    quality_score: 92,
    last_sync: '2026-08-31 10:10:00',
  },
]

const statusColors: Record<string, string> = {
  active: 'success',
  inactive: 'default',
  maintenance: 'warning',
}

const platformColors: Record<string, string> = {
  haul: '#f50',
  walmart: '#1890ff',
  joom: '#52c41a',
  ozon: '#722ed1',
  shopify: '#13c2c2',
}

const columns = [
  {
    title: '平台名称',
    dataIndex: 'name',
    key: 'name',
    width: 200,
    render: (name: string, record: any) => (
      <Space>
        <ShopOutlined style={{ color: platformColors[record.type] }} />
        <Text strong>{name}</Text>
      </Space>
    ),
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 100,
    render: (status: string) => (
      <Tag color={statusColors[status]}>
        {status === 'active' ? '运行中' :
         status === 'inactive' ? '已停用' : '维护中'}
      </Tag>
    ),
  },
  {
    title: '商品数',
    dataIndex: 'products',
    key: 'products',
    width: 100,
    sorter: (a: any, b: any) => a.products - b.products,
  },
  {
    title: '订单数',
    dataIndex: 'orders',
    key: 'orders',
    width: 100,
    sorter: (a: any, b: any) => a.orders - b.orders,
  },
  {
    title: '销售额',
    dataIndex: 'revenue',
    key: 'revenue',
    width: 150,
    render: (revenue: number) => `$${revenue.toFixed(2)}`,
    sorter: (a: any, b: any) => a.revenue - b.revenue,
  },
  {
    title: '增长率',
    dataIndex: 'growth',
    key: 'growth',
    width: 120,
    render: (growth: number) => (
      <Space>
        {growth >= 0 ? (
          <RiseOutlined style={{ color: '#52c41a' }} />
        ) : (
          <FallOutlined style={{ color: '#ff4d4f' }} />
        )}
        <Text style={{ color: growth >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {growth >= 0 ? '+' : ''}{growth}%
        </Text>
      </Space>
    ),
    sorter: (a: any, b: any) => a.growth - b.growth,
  },
  {
    title: '质量分',
    dataIndex: 'quality_score',
    key: 'quality_score',
    width: 150,
    render: (score: number) => (
      <Progress
        percent={score}
        size="small"
        status={score >= 80 ? 'success' : score >= 60 ? 'normal' : 'exception'}
        format={(percent) => `${percent}分`}
      />
    ),
    sorter: (a: any, b: any) => a.quality_score - b.quality_score,
  },
  {
    title: '最后同步',
    dataIndex: 'last_sync',
    key: 'last_sync',
    width: 180,
  },
  {
    title: '操作',
    key: 'action',
    width: 200,
    render: (_: any, record: any) => (
      <Space size="middle">
        <Button type="link" size="small" icon={<BarChartOutlined />} onClick={() => message.info("查看平台详情")}>
          详情
        </Button>
        <Button type="link" size="small" icon={<SyncOutlined />} onClick={() => message.success("同步请求已提交")}>
          同步
        </Button>
        <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => message.info("打开平台设置")}>
          设置
        </Button>
      </Space>
    ),
  },
]

const Platforms: React.FC = () => {
  // 计算汇总数据
  const totalProducts = platformsData.reduce((sum, p) => sum + p.products, 0)
  const totalOrders = platformsData.reduce((sum, p) => sum + p.orders, 0)
  const totalRevenue = platformsData.reduce((sum, p) => sum + p.revenue, 0)
  const avgQuality = Math.round(platformsData.reduce((sum, p) => sum + p.quality_score, 0) / platformsData.length)

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        平台运营
      </Title>

      {/* 汇总统计 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总商品数"
              value={totalProducts}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总订单数"
              value={totalOrders}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总销售额"
              value={totalRevenue}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="平均质量分"
              value={avgQuality}
              suffix="分"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 平台列表 */}
      <Card title="平台列表" extra={
        <Space>
          <Button icon={<SyncOutlined />} onClick={() => message.success("全部平台同步已启动")}>全部同步</Button>
          <Button type="primary" icon={<SettingOutlined />} onClick={() => message.info("打开全局平台设置")}>平台设置</Button>
        </Space>
      }>
        <Table
          columns={columns}
          dataSource={platformsData}
          pagination={false}
          size="middle"
          scroll={{ x: 1400 }}
        />
      </Card>
    </div>
  )
}

export default Platforms
